/**
 * Back In Stock - Klaviyo Integration
 *
 * Handles back-in-stock notification subscriptions via Klaviyo Client API
 * Extends the DialogComponent class from the Horizon theme
 *
 * API Documentation: https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
 */

import { DialogComponent } from '@theme/dialog';

/**
 * Custom element for back-in-stock modal
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLButtonElement} closeButton - Close button
 * @property {HTMLInputElement} emailInput - Email input field
 * @property {HTMLButtonElement} submitButton - Submit button
 * @property {HTMLElement} errorMessage - Error message container
 * @property {HTMLElement} successMessage - Success message container
 *
 * @extends DialogComponent<Refs>
 */
class BackInStockModal extends DialogComponent {
  requiredRefs = ['dialog', 'closeButton', 'emailInput', 'submitButton', 'errorMessage', 'successMessage'];

  constructor() {
    super();

    // Get Klaviyo Public API Key from attribute or fallback
    this.klaviyoPublicKey = this.getAttribute('public-key') || 'RZKS9E';
    this.isSubmitting = false;
  }

  connectedCallback() {
    super.connectedCallback();

    // Store original button text
    const { submitButton } = this.refs;
    if (submitButton && !submitButton.dataset.originalText) {
      submitButton.dataset.originalText = submitButton.textContent.trim();
    }
  }

  /**
   * Handles form submission
   *
   * @param {Event} event - The click event
   */
  async handleSubmit(event) {
    event.preventDefault();

    // Prevent double submission
    if (this.isSubmitting) {
      return;
    }

    const { emailInput, submitButton } = this.refs;
    const email = emailInput.value.trim();
    const variantId = this.getAttribute('data-variant-id');
    const productHandle = this.getAttribute('data-product-handle');

    // Validation
    if (!email) {
      this.showError('Please enter your email address.');
      return;
    }

    if (!this.isValidEmail(email)) {
      this.showError('Please enter a valid email address.');
      return;
    }

    if (!variantId) {
      this.showError('Variant unavailable. Please refresh and try again.');
      return;
    }

    // Set submitting state
    this.isSubmitting = true;
    this.hideError();
    this.setLoadingState(true);

    try {
      await this.subscribeToKlaviyo({ email, variantId, productHandle });
      this.showSuccess();

      // Track event (if Google Analytics or GTM is available)
      this.trackSubscription(productHandle, variantId);

      // Auto-close modal after 3 seconds
      setTimeout(() => {
        this.closeDialog().then(() => {
          // Reset form after closing animation completes
          setTimeout(() => this.resetForm(), 300);
        });
      }, 3000);
    } catch (error) {
      console.error('[Back In Stock] Subscription failed:', error);
      this.showError(error?.message || 'Something went wrong. Please try again.');
      this.setLoadingState(false);
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * Subscribes to Klaviyo back-in-stock notifications
   *
   * @param {Object} params - Subscription parameters
   * @param {string} params.email - Customer email
   * @param {string} params.variantId - Shopify variant ID
   * @param {string} params.productHandle - Product handle for tracking
   * @returns {Promise<boolean>} Success status
   */
  async subscribeToKlaviyo({ email, variantId, productHandle }) {
    if (!this.klaviyoPublicKey) {
      throw new Error('Missing Klaviyo configuration.');
    }

    // Klaviyo requires this specific format for Shopify variants
    const catalogVariantId = `$shopify:::$default:::${variantId}`;

    // Build API payload
    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email
              }
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: catalogVariantId
            }
          }
        }
      }
    };

    // Klaviyo Client API endpoint
    const endpoint = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(this.klaviyoPublicKey)}`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'revision': '2024-10-15'
      },
      body: JSON.stringify(payload)
    });

    // Success status codes
    // 409 = already subscribed (treat as success)
    if ([200, 202, 204, 409].includes(response.status)) {
      return true;
    }

    // Parse error message
    let errorMessage = 'Failed to subscribe. Please try again.';
    try {
      const errorData = await response.json();
      errorMessage = errorData.errors?.[0]?.detail || errorMessage;
    } catch (_) {
      // Use default message if JSON parsing fails
    }

    throw new Error(errorMessage);
  }

  /**
   * Validates email format
   *
   * @param {string} email - Email address to validate
   * @returns {boolean} Whether email is valid
   */
  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Shows error message
   *
   * @param {string} message - Error message to display
   */
  showError(message) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;

    const errorText = errorMessage.querySelector('.error-text');
    if (errorText) {
      errorText.textContent = message;
    }
    errorMessage.style.display = 'flex';
  }

  /**
   * Hides error message
   */
  hideError() {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;

    errorMessage.style.display = 'none';
  }

  /**
   * Shows success message
   */
  showSuccess() {
    const { successMessage, emailInput, submitButton } = this.refs;
    if (!successMessage) return;

    // Hide form fields, show success
    emailInput.style.display = 'none';
    emailInput.closest('.form-field')?.setAttribute('style', 'display: none;');
    submitButton.style.display = 'none';
    this.hideError();

    successMessage.style.display = 'flex';
  }

  /**
   * Sets loading state on submit button
   *
   * @param {boolean} loading - Whether button should show loading state
   */
  setLoadingState(loading) {
    const { submitButton } = this.refs;
    if (!submitButton) return;

    if (loading) {
      submitButton.disabled = true;
      submitButton.classList.add('loading');
      submitButton.setAttribute('aria-busy', 'true');
    } else {
      submitButton.disabled = false;
      submitButton.classList.remove('loading');
      submitButton.removeAttribute('aria-busy');
    }
  }

  /**
   * Resets form to initial state
   */
  resetForm() {
    const { emailInput, submitButton, errorMessage, successMessage } = this.refs;

    // Reset email input
    if (emailInput) {
      emailInput.value = '';
      emailInput.style.display = '';
      emailInput.closest('.form-field')?.setAttribute('style', '');
    }

    // Reset button
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.classList.remove('loading');
      submitButton.removeAttribute('aria-busy');
      submitButton.style.display = '';
    }

    // Hide messages
    if (errorMessage) errorMessage.style.display = 'none';
    if (successMessage) successMessage.style.display = 'none';

    this.isSubmitting = false;
  }

  /**
   * Tracks subscription event in analytics
   *
   * @param {string} productHandle - Product handle
   * @param {string} variantId - Variant ID
   */
  trackSubscription(productHandle, variantId) {
    // Google Analytics 4
    if (window.gtag) {
      window.gtag('event', 'back_in_stock_subscription', {
        'event_category': 'engagement',
        'event_label': productHandle || 'unknown',
        'value': variantId
      });
    }

    // Google Tag Manager
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'backInStockSubscription',
        'productHandle': productHandle,
        'variantId': variantId
      });
    }
  }
}

// Register custom element
if (!customElements.get('back-in-stock-modal')) {
  customElements.define('back-in-stock-modal', BackInStockModal);
}
